import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { numeroDoContato } from '../numero-do-contato';

interface Props {
  /** `ticket.contact.phone` — identificador phone/wa_id do contato. */
  telefone: string | null;
}

/**
 * Número do contato na linha das etiquetas do cabeçalho da conversa: mostra
 * formatado e um clique copia só DDD + número (vide `numeroDoContato`).
 *
 * É um <button> dentro da área das etiquetas de propósito: o clique no
 * cabeçalho abre as informações do contato, e o cabeçalho ignora clique em
 * botão dessa área — copiar não abre o modal.
 *
 * Quem decide se aparece é o cabeçalho: na fila cega (conversa ainda não
 * aceita) ele nem é montado, pra não revelar o número.
 */
export function CopiarNumero({ telefone }: Props) {
  const numero = numeroDoContato(telefone);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 1500);
    return () => clearTimeout(t);
  }, [copiado]);

  if (!numero) return null;

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(numero.copiar);
      setCopiado(true);
      toast.success('Número copiado', { description: numero.copiar, duration: 2000 });
    } catch {
      toast.error('Não foi possível copiar o número');
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copiar()}
      title="Copiar Telefone"
      aria-label={`Copiar número ${numero.exibir}`}
      className="inline-flex h-[19px] items-center gap-1 rounded-sm border border-n-200 px-2 font-semibold text-[10px] text-n-600 hover:border-brand-500 hover:text-brand-600"
    >
      {numero.exibir}
      {copiado ? (
        <Check size={11} strokeWidth={2.5} className="text-success-600" />
      ) : (
        <Copy size={11} strokeWidth={2} />
      )}
    </button>
  );
}
